/**
 * Garden Object Type System
 *
 * This module defines the unified type system for all garden objects.
 * It replaces the old fragmented approach where different object types
 * used inconsistent property naming (drawingType, shapeType, etc.).
 */

// ============================================================================
// Object Type Constants
// ============================================================================

/**
 * All possible garden object types
 * @enum {string}
 */
export const GARDEN_OBJECT_TYPES = {
  // Basic shapes
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  LINE: 'line',

  // Complex shapes
  POLYGON: 'polygon',
  BEZIER_CURVE: 'bezier_curve',

  // Garden-specific objects
  HEIGHT_ZONE: 'height_zone',
  BACKGROUND_IMAGE: 'background_image',

  // Future object types (not yet implemented)
  PLANT: 'plant',
  FURNITURE: 'furniture',
  WATER_FEATURE: 'water_feature',
  PATHWAY: 'pathway',
  LAWN_AREA: 'lawn_area',
  PLANTING_BED: 'planting_bed',
};

/**
 * Object categories for grouping related types
 * @enum {string}
 */
export const OBJECT_CATEGORIES = {
  BASIC_SHAPE: 'basic_shape',
  COMPLEX_SHAPE: 'complex_shape',
  GARDEN_ELEMENT: 'garden_element',
  ANNOTATION: 'annotation',
  BACKGROUND: 'background',
};

/**
 * Map object types to their categories
 */
export const OBJECT_TYPE_CATEGORIES = {
  [GARDEN_OBJECT_TYPES.RECTANGLE]: OBJECT_CATEGORIES.BASIC_SHAPE,
  [GARDEN_OBJECT_TYPES.CIRCLE]: OBJECT_CATEGORIES.BASIC_SHAPE,
  [GARDEN_OBJECT_TYPES.LINE]: OBJECT_CATEGORIES.BASIC_SHAPE,
  [GARDEN_OBJECT_TYPES.POLYGON]: OBJECT_CATEGORIES.COMPLEX_SHAPE,
  [GARDEN_OBJECT_TYPES.BEZIER_CURVE]: OBJECT_CATEGORIES.COMPLEX_SHAPE,
  [GARDEN_OBJECT_TYPES.HEIGHT_ZONE]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.BACKGROUND_IMAGE]: OBJECT_CATEGORIES.BACKGROUND,
  [GARDEN_OBJECT_TYPES.PLANT]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.FURNITURE]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.WATER_FEATURE]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.PATHWAY]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.LAWN_AREA]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
  [GARDEN_OBJECT_TYPES.PLANTING_BED]: OBJECT_CATEGORIES.GARDEN_ELEMENT,
};

// ============================================================================
// Default Render Options
// ============================================================================

/**
 * Default render options for each object type
 * These define the default visual appearance when creating new objects
 */
export const DEFAULT_RENDER_OPTIONS = {
  [GARDEN_OBJECT_TYPES.RECTANGLE]: {
    fill: 'rgba(139, 69, 19, 0.3)',      // Brown, semi-transparent
    stroke: '#8B4513',                    // Solid brown
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.CIRCLE]: {
    fill: 'rgba(34, 139, 34, 0.3)',      // Forest green, semi-transparent
    stroke: '#228B22',                    // Solid forest green
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.LINE]: {
    stroke: '#000000',                    // Black
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.POLYGON]: {
    fill: 'rgba(70, 130, 180, 0.3)',     // Steel blue, semi-transparent
    stroke: '#4682B4',                    // Solid steel blue
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.BEZIER_CURVE]: {
    stroke: '#8B4513',                    // Brown
    strokeWidth: 2,
    fill: 'transparent',
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.HEIGHT_ZONE]: {
    fill: 'rgba(255, 165, 0, 0.2)',      // Orange, very transparent
    stroke: '#FF8C00',                    // Dark orange
    strokeWidth: 2,
    strokeDashArray: [5, 5],              // Dashed line
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.BACKGROUND_IMAGE]: {
    opacity: 0.5,                         // Semi-transparent by default
    selectable: false,                    // Not selectable by default
    evented: false,                       // No events by default
  },

  // Future object types (placeholders)
  [GARDEN_OBJECT_TYPES.PLANT]: {
    fill: 'rgba(0, 128, 0, 0.4)',        // Green, semi-transparent
    stroke: '#008000',
    strokeWidth: 1,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.FURNITURE]: {
    fill: 'rgba(160, 82, 45, 0.5)',      // Sienna, semi-transparent
    stroke: '#A0522D',
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.WATER_FEATURE]: {
    fill: 'rgba(0, 191, 255, 0.4)',      // Deep sky blue, semi-transparent
    stroke: '#00BFFF',
    strokeWidth: 2,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.PATHWAY]: {
    fill: 'rgba(169, 169, 169, 0.5)',    // Dark gray, semi-transparent
    stroke: '#696969',
    strokeWidth: 1,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.LAWN_AREA]: {
    fill: 'rgba(124, 252, 0, 0.3)',      // Lawn green, semi-transparent
    stroke: '#7CFC00',
    strokeWidth: 1,
    opacity: 1,
  },

  [GARDEN_OBJECT_TYPES.PLANTING_BED]: {
    fill: 'rgba(210, 180, 140, 0.4)',    // Tan (soil color), semi-transparent
    stroke: '#D2B48C',
    strokeWidth: 2,
    opacity: 1,
  },
};

// ============================================================================
// Property Definitions
// ============================================================================

/**
 * Define which properties are available for each object type
 * This helps the unified properties panel know what to display
 */
export const OBJECT_PROPERTIES = {
  [GARDEN_OBJECT_TYPES.RECTANGLE]: {
    geometry: ['width', 'height', 'left', 'top', 'angle'],
    appearance: [],
    advanced: [],
  },

  [GARDEN_OBJECT_TYPES.CIRCLE]: {
    geometry: ['radius', 'left', 'top'],
    appearance: [],
    advanced: [],
  },

  [GARDEN_OBJECT_TYPES.LINE]: {
    geometry: ['x1', 'y1', 'x2', 'y2', 'length'],
    appearance: [],
    advanced: [],
  },

  [GARDEN_OBJECT_TYPES.POLYGON]: {
    geometry: ['points', 'left', 'top', 'angle'],
    appearance: [],
    advanced: [],
  },

  [GARDEN_OBJECT_TYPES.BEZIER_CURVE]: {
    geometry: ['points', 'left', 'top'],
    appearance: [],
    advanced: [],
  },

  [GARDEN_OBJECT_TYPES.HEIGHT_ZONE]: {
    geometry: ['points', 'left', 'top', 'angle'],
    appearance: [],
    advanced: ['heightDifference', 'label'],
  },

  [GARDEN_OBJECT_TYPES.BACKGROUND_IMAGE]: {
    geometry: ['width', 'height', 'left', 'top', 'scaleX', 'scaleY'],
    appearance: [],
    advanced: [],
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if an object is a specific type
 * @param {Object} obj - Fabric.js object
 * @param {string} type - Object type to check
 * @returns {boolean}
 */
export function isObjectType(obj, type) {
  return obj?.objectType === type;
}

/**
 * Check if an object belongs to a specific category
 * @param {Object} obj - Fabric.js object
 * @param {string} category - Category to check
 * @returns {boolean}
 */
export function isObjectCategory(obj, category) {
  const objectCategory = OBJECT_TYPE_CATEGORIES[obj?.objectType];
  return objectCategory === category;
}

/**
 * Get default render options for a specific object type
 * @param {string} type - Object type
 * @returns {Object} Render options
 */
export function getDefaultRenderOptions(type) {
  // Spread on undefined yields {}, so the previous `|| {}` was dead code.
  return { ...DEFAULT_RENDER_OPTIONS[type] };
}

/**
 * Get available properties for an object type
 * @param {string} type - Object type
 * @returns {Object} Object with geometry, appearance, and advanced property arrays
 */
export function getObjectProperties(type) {
  return OBJECT_PROPERTIES[type] || { geometry: [], appearance: [], advanced: [] };
}

/**
 * Check if an object has a specific property
 * @param {string} type - Object type
 * @param {string} property - Property name
 * @returns {boolean}
 */
export function hasObjectProperty(type, property) {
  const props = OBJECT_PROPERTIES[type];
  if (!props) return false;

  return props.geometry.includes(property) ||
         props.appearance.includes(property) ||
         props.advanced.includes(property);
}

/**
 * Create a new garden object with type metadata
 * @param {string} type - Object type
 * @param {Object} fabricObject - Fabric.js object instance
 * @param {Object} additionalData - Additional metadata
 * @returns {Object} Fabric.js object with metadata
 */
export function createGardenObject(type, fabricObject, additionalData = {}) {
  const renderOptions = getDefaultRenderOptions(type);

  // Only apply default render options for properties that are NOT already set
  // This allows config-driven styles (from gardenObjectTypes.js config) to take precedence
  const currentFill = fabricObject.get('fill');
  const currentStroke = fabricObject.get('stroke');
  const currentStrokeWidth = fabricObject.get('strokeWidth');
  const currentOpacity = fabricObject.get('opacity');

  const optionsToApply = { ...renderOptions };

  // Don't override if already set to a non-default value
  if (currentFill !== undefined && currentFill !== null) {
    delete optionsToApply.fill;
  }
  if (currentStroke !== undefined && currentStroke !== null) {
    delete optionsToApply.stroke;
  }
  if (currentStrokeWidth !== undefined && currentStrokeWidth !== null) {
    delete optionsToApply.strokeWidth;
  }
  if (currentOpacity !== undefined && currentOpacity !== null) {
    delete optionsToApply.opacity;
  }

  // Apply remaining default render options
  fabricObject.set(optionsToApply);

  // Add metadata - explicitly handle data property
  const { data: customData, ...otherData } = additionalData;

  fabricObject.set({
    objectType: type,
    renderOptions,
    ...otherData,
  });

  // Set data directly as a property (Fabric.js doesn't handle nested objects well with set())
  if (customData) {
    fabricObject.data = customData;
  }

  return fabricObject;
}

/**
 * Migrate old object format to new format
 * Handles legacy objects with drawingType, shapeType, etc.
 * @param {Object} obj - Fabric.js object or plain object
 * @returns {Object} Migrated object
 */
export function migrateObjectToNewFormat(obj) {
  if (!obj) return obj;

  // If already has objectType, no migration needed
  if (obj.objectType) return obj;

  // Detect old format and map to new type
  let objectType = null;

  if (obj.drawingType === 'rectangle' || obj.type === 'rect') {
    objectType = GARDEN_OBJECT_TYPES.RECTANGLE;
  } else if (obj.drawingType === 'circle' || obj.type === 'circle') {
    objectType = GARDEN_OBJECT_TYPES.CIRCLE;
  } else if (obj.drawingType === 'line' || obj.type === 'line') {
    objectType = GARDEN_OBJECT_TYPES.LINE;
  } else if (obj.shapeType === 'polygon' || (obj.type === 'polygon' && obj.points)) {
    objectType = GARDEN_OBJECT_TYPES.POLYGON;
  } else if (obj.shapeType === 'bezier_curve' || obj.curveType === 'bezier') {
    objectType = GARDEN_OBJECT_TYPES.BEZIER_CURVE;
  } else if (obj.shapeType === 'height_zone' || obj.heightDifference !== undefined) {
    objectType = GARDEN_OBJECT_TYPES.HEIGHT_ZONE;
  } else if (obj.type === 'image') {
    objectType = GARDEN_OBJECT_TYPES.BACKGROUND_IMAGE;
  }

  // If we identified a type, add metadata
  if (objectType) {
    const renderOptions = {
      fill: obj.fill,
      stroke: obj.stroke,
      strokeWidth: obj.strokeWidth,
      opacity: obj.opacity,
    };

    // Check if this is a Fabric.js object (has .set method) or plain object
    if (typeof obj.set === 'function') {
      // Fabric.js object - use .set() method
      obj.set({
        objectType,
        renderOptions,
      });
    } else {
      // Plain object (from JSON/localStorage) - directly assign properties
      obj.objectType = objectType;
      obj.renderOptions = renderOptions;
    }
  }

  return obj;
}
